/**
 * Portfolio: Balances, Positions, Open orders, Order history, Trades, Settled (#788) for the viewed account
 * (connected wallet or ?ghost= address). The last three panels live in web/src/account/orders/
 * (built separately); until they land, the slots below render placeholders.
 */
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isPortfolioTab, PortfolioTabList, PortfolioTabPanel, type PortfolioTabId } from '../account/PortfolioTabs';
import { shortAddress } from '../wallet/address';
import { useGhost, useViewedAccount } from '../wallet/context';
import { useT } from '../i18n/useT';

function NoAccount() {
  const { enterGhost, ghostError } = useGhost();
  const t = useT();
  const [input, setInput] = useState('');
  const [bad, setBad] = useState(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBad(!enterGhost(input.trim()));
  };
  return (
    <div className="card space-y-3 p-6 text-sm" data-testid="portfolio-empty">
      <p className="text-muted">{t('portfolio.empty')}</p>
      <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
        <input
          aria-label={t('portfolio.ghost_input')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('portfolio.ghost_placeholder')}
          className="num min-w-0 flex-1 rounded-md border border-border bg-surface-2 px-3 py-2"
        />
        <button type="submit" className="rounded-md bg-accent/20 px-3 py-2 font-medium text-accent hover:bg-accent/30">
          {t('portfolio.watch')}
        </button>
      </form>
      {(bad || ghostError) && <p className="text-danger">{ghostError ?? t('portfolio.not_valid')}</p>}
    </div>
  );
}

export default function PortfolioPage() {
  const { address, mode } = useViewedAccount();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: PortfolioTabId = isPortfolioTab(raw) ? raw : 'balances';
  const select = (id: PortfolioTabId) => {
    const next = new URLSearchParams(params);
    if (id === 'balances') next.delete('tab');
    else next.set('tab', id);
    setParams(next, { replace: true });
  };

  return (
    <section className="space-y-4" data-tour="portfolio">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold sm:text-2xl">{t('nav.portfolio')}</h1>
        {address && (
          <span className="num text-sm text-muted" data-testid="portfolio-account">
            {mode === 'ghost' ? t('portfolio.watching', { address: shortAddress(address) }) : shortAddress(address)}
          </span>
        )}
      </div>

      {!address ? (
        <NoAccount />
      ) : (
        <>
          <PortfolioTabList tab={tab} onSelect={select} idPrefix="portfolio" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" />
          {/* key={address}: switching account remounts panels so no stale rows flash. */}
          <div key={address}>
            <PortfolioTabPanel tab={tab} address={address} idPrefix="portfolio" />
          </div>
        </>
      )}
    </section>
  );
}
